import { useCallback } from 'react';
import {
    Button, Table,
} from 'reactstrap';
import { v7 as uuid } from 'uuid';
import { isEmpty } from 'lodash-es';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle, faTrash } from '@fortawesome/free-solid-svg-icons';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { NumericInput, SmallButton } from '../common/forms/FormElements';
import { Modal } from '../common/Modal';

class WorkTypePerDiem {
    id = uuid();
    contractUnionWorkTypeId = 0;
    workTypeName = '';
    perDiemRate = 0;
}

export const USStateWorkTypePerDiemGrid = ({
    workTypes,
    perDiemRates,
    onPerDiemRatesChange,
}) => {
    const handleSubmit = useCallback((newEntry) => {
        const workType = workTypes.find((wt) => wt.value === newEntry.contractUnionWorkTypeId);
        const entry = {
            ...new WorkTypePerDiem(),
            ...newEntry,
            workTypeName: workType?.label ?? '',
        };
        onPerDiemRatesChange([...perDiemRates, entry]);
    }, [perDiemRates, onPerDiemRatesChange, workTypes]);

    const handleRemove = useCallback((id) => {
        onPerDiemRatesChange(perDiemRates.filter((r) => r.id !== id));
    }, [perDiemRates, onPerDiemRatesChange]);

    const handleAdd = useCallback(() => {
        const addedWorkTypeIds = perDiemRates.map((r) => r.contractUnionWorkTypeId);
        const availableWorkTypes = workTypes.filter(
            (wt) => wt.isUnionWork && !addedWorkTypeIds.includes(wt.value),
        );

        Modal.show({
            title: 'Add Per-Work-Type Per Diem Override',
            onSubmit: (values) => handleSubmit(values),
            body: () => (
                <>
                    <ValidatedSelect
                        id="contractUnionWorkTypeId"
                        name="contractUnionWorkTypeId"
                        required
                        options={availableWorkTypes}
                        error="A work type selection is required."
                        label="Work Type"
                    />

                    <NumericInput
                        id="perDiemRate"
                        name="perDiemRate"
                        label="Per Diem Rate"
                        required
                        step={0.01}
                        min={0}
                        error="A per diem rate is required."
                    />
                </>
            ),
        });
    }, [handleSubmit, workTypes, perDiemRates]);

    return (
        <div>
            <div className="d-flex justify-content-end mb-1">
                <SmallButton
                    type="button"
                    onClick={handleAdd}
                >
                    <FontAwesomeIcon
                        icon={faPlusCircle}
                        size="md"
                        className="mr-2"
                    />
                    Add Override
                </SmallButton>
            </div>

            <Table
                className="table table-sm"
                bordered
                responsive
            >
                <thead>
                    <tr>
                        <th>Work Type</th>
                        <th>Per Diem Rate</th>
                        <th className="text-center">
                            <FontAwesomeIcon icon={faTrash} />
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {isEmpty(perDiemRates) ? (
                        <tr>
                            <td
                                colSpan={3}
                                className="text-center"
                            >
                                No per-work-type per diem overrides
                            </td>
                        </tr>
                    ) : (
                        perDiemRates.map((rate) => (
                            <tr key={rate.id}>
                                <td>{rate.workTypeName}</td>
                                <td>{rate.perDiemRate}</td>
                                <td
                                    className="text-center"
                                    style={{ width: '30px' }}
                                >
                                    <Button
                                        type="button"
                                        color="outline-primary"
                                        onClick={() => handleRemove(rate.id)}
                                        size="sm"
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </Button>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </Table>
        </div>
    );
};
